import { Box, Typography, IconButton, Chip, Tooltip } from "@mui/material";
import { SubmitErrorPanelProps } from "../../../../types/cashout.types";
import AssetErrorRow from "./AssetErrorRow";
import {
    Close as CloseIcon,
    ErrorOutline as ErrorOutlineIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';

function SubmitErrorPanel({ submitError, onDismiss }: SubmitErrorPanelProps) {
    const hasPartialSuccess = submitError.success.length > 0;

    return (
        <Box
            sx={{
                backgroundColor: hasPartialSuccess
                    ? 'rgba(255, 193, 7, 0.08)'
                    : 'rgba(255, 107, 107, 0.08)',
                border: `1px solid ${hasPartialSuccess ? 'rgba(255,193,7,0.3)' : 'rgba(255,107,107,0.3)'}`,
                borderRadius: '12px',
                overflow: 'hidden',
                pr: { xs: '8px', sm: '12px' },
            }}
        >
            {/* Header row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px 8px',
                    borderBottom: `1px solid ${hasPartialSuccess ? 'rgba(255,193,7,0.15)' : 'rgba(255,107,107,0.15)'}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {hasPartialSuccess ? (
                        <WarningAmberIcon sx={{ fontSize: '18px', color: '#FFC107' }} />
                    ) : (
                        <ErrorOutlineIcon sx={{ fontSize: '18px', color: '#FF6B6B' }} />
                    )}
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: hasPartialSuccess ? '#FFC107' : '#FF6B6B',
                            fontFamily: 'Avenir',
                        }}
                    >
                        {hasPartialSuccess
                            ? `${submitError.success.length} succeeded · ${submitError.failed.length} failed`
                            : `${submitError.failed.length} asset${submitError.failed.length !== 1 ? 's' : ''} failed`
                        }
                    </Typography>
                </Box>

                <IconButton
                    onClick={onDismiss}
                    size="small"
                    sx={{ width: '22px', height: '22px', padding: 0, color: 'rgba(255,255,255,0.4)' }}
                >
                    <CloseIcon sx={{ fontSize: '16px' }} />
                </IconButton>
            </Box>

            {/* Partial success summary */}
            {hasPartialSuccess && (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        padding: '10px 14px 6px',
                    }}
                >
                    {submitError.success.map((item: any, i: number) => (
                        <Tooltip
                            key={i}
                            title={item.processingNote ?? 'Processed successfully'}
                            arrow
                        >
                            <Chip
                                icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important', color: '#4EDF6D !important' }} />}
                                label={item.processingNote ? `${item.asset} ⏳` : item.asset}
                                size="small"
                                sx={{
                                    backgroundColor: item.processingNote
                                        ? 'rgba(255,193,7,0.12)'
                                        : 'rgba(78, 223, 109, 0.12)',
                                    color: item.processingNote ? '#FFC107' : '#4EDF6D',
                                    fontSize: '11px',
                                    fontFamily: 'Avenir',
                                    border: `1px solid ${item.processingNote ? 'rgba(255,193,7,0.25)' : 'rgba(78,223,109,0.25)'}`,
                                    height: '22px',
                                }}
                            />
                        </Tooltip>
                    ))}
                </Box>
            )}

            {/* Failure list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '8px 14px 12px' }}>
                {submitError.failed.map((failure, index) => (
                    <AssetErrorRow key={index} failure={failure} />
                ))}
            </Box>
        </Box>
    );
}

export default SubmitErrorPanel;